/**
 * streamingFileDumpMask.ts — R14 (2026-08-13 datacenter-hvac follow-up).
 *
 * While a deliverable turn streams, the raw file dump (a filename label line
 * followed by a fenced block or a bare HTML document) used to render straight
 * into the chat bubble — a wall of code the user was never meant to read,
 * which then either vanished (turn died at a ceiling) or was replaced by the
 * attachment card at finalize. Owner directive: the attachment element is the
 * ONLY acceptable surface for generated files — raw code never belongs in a
 * message, including mid-stream.
 *
 * This pure module rewrites the STREAMED VIEW only: the dump region is
 * replaced with a one-line "Generating <name>…" placeholder. The underlying
 * accumulated text is never modified — the server's finalize (inline-file
 * lift → pointer + attachment card) still owns the final message via
 * finalTextOverride, and non-deliverable code snippets (no filename label)
 * are left untouched because the server will not lift those either.
 *
 * Mirrors the server's extraction contract (inlineFileDeliverable.ts):
 * FILENAME_LABEL_RE + a fence or full-document opener within the next lines.
 */

/** Mirror of the server's FILENAME_LABEL_RE (inlineFileDeliverable.ts). */
const FILENAME_LABEL_RE = /^\s*(?:\*\*|`)?([A-Za-z0-9][A-Za-z0-9._-]{0,80}\.[A-Za-z0-9]{1,8})(?:\*\*|`)?\s*:?\s*$/;

const FENCE_OPEN_RE = /^\s*```/;
const HTML_DOC_OPEN_RE = /^\s*(?:<!DOCTYPE\s+html|<html[\s>])/i;

interface DumpRegion {
  name: string;
  /** Character offset where the label line starts. */
  start: number;
  /** Character offset just past the dump (fence close) — or text.length for
   *  a still-streaming / unfenced dump. */
  end: number;
}

/**
 * Find the first labeled file dump in the text: a filename label line whose
 * next 1-2 non-empty lines open a fence or a full HTML document.
 */
function findLabeledDump(text: string): DumpRegion | null {
  const lines = text.split('\n');
  let offset = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const label = FILENAME_LABEL_RE.exec(line);
    if (label) {
      // Look ahead up to 2 non-empty lines for the dump opener.
      let cursor = offset + line.length + 1;
      for (let j = i + 1, seen = 0; j < lines.length && seen < 2; j += 1) {
        const next = lines[j];
        if (!next.trim()) {
          cursor += next.length + 1;
          continue;
        }
        seen += 1;
        if (FENCE_OPEN_RE.test(next)) {
          return { name: label[1], start: offset, end: findFenceEnd(lines, j, cursor, text) };
        }
        if (HTML_DOC_OPEN_RE.test(next)) {
          // Unfenced document — runs to the end of the message (R12 shape).
          return { name: label[1], start: offset, end: text.length };
        }
        cursor += next.length + 1;
      }
    }
    offset += line.length + 1;
  }
  return null;
}

/** End offset of a fenced block opened at lines[openIdx] (or text.length while unclosed). */
function findFenceEnd(lines: string[], openIdx: number, openOffset: number, text: string): number {
  let offset = openOffset + lines[openIdx].length + 1;
  for (let j = openIdx + 1; j < lines.length; j += 1) {
    if (/^\s*```\s*$/.test(lines[j])) {
      return Math.min(offset + lines[j].length, text.length);
    }
    offset += lines[j].length + 1;
  }
  return text.length;
}

/**
 * The streamed-view transform. Returns the text with the first labeled dump
 * region replaced by a generating-placeholder line, or the input unchanged
 * when no labeled dump is present.
 */
export function maskStreamingFileDump(text: string): string {
  if (!text || (!text.includes('```') && !/<!DOCTYPE\s+html|<html[\s>]/i.test(text))) {
    return text;
  }
  const dump = findLabeledDump(text);
  if (!dump) return text;
  // R17 (2026-08-15): prose dumps (.md/.txt) may legitimately finalize as
  // chat text instead of an attachment — the server's format policy can veto
  // the file and unwrap the content (inlineFileDeliverable.planProseDeliverable).
  // Their placeholder must not promise an attachment card that may never
  // come; code/HTML dumps always lift, so they keep the attachment promise.
  const isProse = /\.(md|txt)$/i.test(dump.name);
  const placeholder = isProse
    ? '⚙️ Putting the full answer together — it will appear here in a moment…'
    : `⚙️ Generating **${dump.name}** — it will be attached below when it's ready…`;
  const before = text.slice(0, dump.start).replace(/\s+$/, '');
  const after = text.slice(dump.end).replace(/^\s+/, '');
  return [before, placeholder, after].filter(Boolean).join('\n\n');
}
